import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Group, ActionIcon, Tooltip, Text, Modal, Grid, Button,
} from '@mantine/core';
import {
  IconRefresh, IconEye, IconPencil, IconPlus,
} from '@tabler/icons-react';
import { CCMSDataTable, TextInput } from '@/components';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import { toast } from '@/components/ui/providers/AlertProvider';
import CreateSessionDrawer from './CreateSessionDrawer';
import { ColorBadge } from '../Components/ColorBadge';
import LeaderSessionDetails from '../Components/LeaderSessionDetails';
import LeaderSessionNotesPanel from '../Components/LeaderSessionNotesPanel';
import {
  useLeaderPortalService,
  LeaderSessionView,
} from '@actschurch/ccms-sdk';

interface SessionTabProps {
  caseID: number;
  active: boolean;
  currentUserId?: string;
  /**
   * ⚠️ No longer used for filtering — the server scopes by `caseID` now. Kept on the props so the
   * call site needs no change, and so nobody re-adds a name-based filter thinking it was dropped.
   */
  caseName?: string | null;
}

export default function SessionTab({ caseID, active, currentUserId }: SessionTabProps) {
  const leaderPortalService = useLeaderPortalService();

  const [allSessions, setAllSessions] = useState<LeaderSessionView[]>([]);
  const [loading, setLoading]   = useState(false);

  const [detailsOpen, setDetailsOpen]         = useState(false);
  const [selectedSession, setSelectedSession] = useState<LeaderSessionView | null>(null);

  const [searchTerm, setSearchTerm] = useState('');

  const [formOpen, setFormOpen]             = useState(false);
  const [editingSession, setEditingSession] = useState<LeaderSessionView | null>(null);

  const [sortConfig, setSortConfig]   = useState<SortConfig | null>(null);

  /**
   * The leader's sessions **for this case**, scoped server-side.
   *
   * ⚠️ This used to call `mySessions()` unscoped and then filter by **case-name substring**, which was
   * wrong rather than merely slow: two cases whose names share a substring cross-contaminated each
   * other's lists, and renaming a case silently emptied its own tab. `mySessions(caseId)` (SDK 0.0.50)
   * removed the guesswork — do not reintroduce a name filter here.
   */
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await leaderPortalService.mySessions(caseID);
      const rows: LeaderSessionView[] = res?.data ?? res ?? [];
      setAllSessions(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('Error fetching sessions:', e);
      toast.error('Error', 'Could not load sessions.');
    } finally {
      setLoading(false);
    }
  }, [leaderPortalService, caseID]);

  useEffect(() => { if (active) fetchSessions(); }, [active, fetchSessions]);

  // Already scoped to this case by the server, so nothing to filter.
  const sessions = allSessions;

  const openDetails = (session: LeaderSessionView) => {
    setSelectedSession(session);
    setDetailsOpen(true);
  };

  const openCreate = () => {
    setEditingSession(null);
    setFormOpen(true);
  };

  const openEdit = (session: LeaderSessionView) => {
    setEditingSession(session);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingSession(null);
  };

  // ── Client-side sort ───────────────────────────────────────────────────────
  const sortedSessions = useMemo(() => {
    if (!sortConfig) return sessions;
    return [...sessions].sort((a, b) => {
      const getValue = (row: LeaderSessionView) => {
        switch (sortConfig.field) {
          case 'name':        return (row.name        ?? '').toLowerCase();
          case 'typeName':    return (row.typeName     ?? '').toLowerCase();
          case 'sessionDate': return String(row.sessionDateTime ?? '');
          case 'statusName':  return (row.statusName   ?? '').toLowerCase();
          default:             return '';
        }
      };
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (aVal === bVal) return 0;
      return aVal > bVal
        ? (sortConfig.direction === 'asc' ? 1 : -1)
        : (sortConfig.direction === 'asc' ? -1 : 1);
    });
  }, [sessions, sortConfig]);

  const searchedSessions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sortedSessions;
    return sortedSessions.filter((row) =>
      [row.name, row.typeName, row.statusName, row.locationName, row.caseNames]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [sortedSessions, searchTerm]);

  // ── Column definitions ──────────────────────────────────────────────────────
  const columns: TableColumn<LeaderSessionView>[] = [
    {
      key: 'name',
      header: 'Name',
      accessor: (row) => row.name ?? '—',
      sortable: true,
      render: (value) => <Text size="sm" fw={500}>{value as string}</Text>,
    },
    {
      key: 'typeName',
      header: 'Type',
      accessor: (row) => row.typeName ?? '—',
      sortable: true,
      render: (_, row) => <ColorBadge label={row.typeName ?? 'N/A'} color={row.typeColor} />,
    },
    {
      key: 'sessionDate',
      header: 'Date',
      accessor: (row) => row.sessionDateDisplay ?? '—',
      sortable: true,
      render: (value) => <Text size="sm">{value as string}</Text>,
    },
    {
      key: 'statusName',
      header: 'Status',
      accessor: (row) => row.statusName ?? '—',
      sortable: true,
      align: 'center',
      render: (_, row) => <ColorBadge label={row.statusName ?? 'N/A'} color={row.statusColor} />,
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (row) => row,
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Group gap={4} justify="center" wrap="nowrap">
          <Tooltip label="View session">
            <ActionIcon
              variant="light"
              color="blue"
              onClick={(e) => { e.stopPropagation(); openDetails(row); }}
            >
              <IconEye size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Update session">
            <ActionIcon
              variant="light"
              color="gray"
              onClick={(e) => { e.stopPropagation(); openEdit(row); }}
            >
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
          {/* ⚠️ No "delete session" action — leaderPortalService doesn't expose a
              delete-session endpoint, only addCaseSession/updateSession. */}
        </Group>
      ),
    },
  ];

  return (
    <>
      <Group justify="space-between" mb="md" gap="xs">
        <TextInput placeholder="Search sessions..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />

        <Group gap={4}>
          <Tooltip label="Refresh sessions" withArrow>
            <ActionIcon variant="subtle" onClick={fetchSessions} loading={loading} size="lg">
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Add Session
          </Button>
        </Group>
      </Group>

      <CCMSDataTable
        data={searchedSessions}
        columns={columns}
        loading={loading}
        sortable
        onSort={(config) => setSortConfig(config)}
        onRowClick={(row) => openDetails(row)}
        striped
        highlightOnHover
      />

      {selectedSession && (
        <Modal
          opened={detailsOpen}
          onClose={() => { setDetailsOpen(false); setSelectedSession(null); }}
          title={selectedSession.name ?? 'Session Details'}
          size="90%"
          centered
        >
          <Grid gutter="xl">
            {/* ── Left: session details ────────────────────────────────── */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <LeaderSessionDetails session={selectedSession} />
            </Grid.Col>

            {/* ── Right: notes ─────────────────────────────────────────── */}
            <Grid.Col span={{ base: 12, md: 6 }}>
              <LeaderSessionNotesPanel
                sessionID={selectedSession.sessionID}
                active={detailsOpen}
                currentUserId={currentUserId}
              />
            </Grid.Col>
          </Grid>
        </Modal>
      )}

      <CreateSessionDrawer
        opened={formOpen}
        onClose={closeForm}
        caseID={caseID}
        session={editingSession}
        onSaved={fetchSessions}
      />
    </>
  );
}